import { Eye, Loader2, MessageSquare } from "lucide-react";
import React from "react";

import { Badge } from "@/components/ui/badge/badge";
import { useI18n } from "@/context/i18n";
import { cn } from "@/lib/utils";

import type {
  TWhatsappConversationMessage,
  TWhatsappConversationThread,
} from "../partner-whatsapp-inbox.types";
import { formatPhoneDisplay } from "../partner-whatsapp-inbox.utils";
import { InboxEmptyState } from "./inbox-empty-state";
import { MessageBubble } from "./message-bubble";
import { ThreadAvatar } from "./thread-avatar";

const CHAT_WALLPAPER =
  "bg-[#e5ddd5] dark:bg-[#0b141a] [background-image:radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.04)_1px,transparent_0)] dark:[background-image:radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.03)_1px,transparent_0)] [background-size:24px_24px]";

export interface MessagePaneProps {
  thread: TWhatsappConversationThread | null;
  messages: TWhatsappConversationMessage[];
  isLoading?: boolean;
  isFetching?: boolean;
}

export const MessagePane: React.FC<MessagePaneProps> = ({
  thread,
  messages,
  isLoading,
  isFetching,
}) => {
  const { t, locale } = useI18n();
  const dateLocale = locale === "en-US" ? "en-US" : "pt-BR";
  const endRef = React.useRef<HTMLDivElement>(null);
  const prevCountRef = React.useRef(0);

  React.useEffect(() => {
    if (messages.length > prevCountRef.current) {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevCountRef.current = messages.length;
  }, [messages]);

  if (!thread) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-muted/10">
        <InboxEmptyState
          icon={MessageSquare}
          title={t("partnerWhatsappInbox.messages.selectThreadTitle")}
          description={t("partnerWhatsappInbox.messages.selectThread")}
        />
      </div>
    );
  }

  const title =
    thread.display_name?.trim() || formatPhoneDisplay(thread.phone_e164);

  return (
    <div className="flex flex-col h-full min-h-0">
      <header className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-border/80 bg-card shadow-sm z-10">
        <ThreadAvatar displayName={thread.display_name} phone={thread.phone_e164} />
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-sm truncate">{title}</h2>
          <p className="text-xs text-muted-foreground truncate">
            {formatPhoneDisplay(thread.phone_e164)}
          </p>
        </div>
        <Badge
          variant="secondary"
          className="shrink-0 gap-1 text-[10px] font-normal hidden sm:flex"
        >
          <Eye className="h-3 w-3" />
          {t("partnerWhatsappInbox.messages.readOnlyBadge")}
        </Badge>
        {isFetching && !isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
        ) : null}
      </header>

      <div
        className={cn(
          "flex-1 overflow-y-auto min-h-0 px-4 py-4 space-y-3",
          CHAT_WALLPAPER,
        )}
      >
        {isLoading && messages.length === 0 ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : null}

        {!isLoading && messages.length === 0 ? (
          <div className="flex justify-center py-8">
            <div className="rounded-xl bg-card/90 dark:bg-card/80 border px-6 py-4 shadow-sm text-center max-w-sm">
              <p className="text-sm text-muted-foreground">
                {t("partnerWhatsappInbox.messages.empty")}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {t("partnerWhatsappInbox.messages.readOnlyHint")}
              </p>
            </div>
          </div>
        ) : null}

        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            direction={msg.direction}
            body={msg.body}
            sentAt={msg.sent_at}
            source={msg.source}
            locale={dateLocale}
            translate={t}
          />
        ))}
        <div ref={endRef} className="h-1" />
      </div>

      <footer className="shrink-0 border-t border-border/80 bg-card/95 px-4 py-2.5">
        <p className="text-[11px] text-center text-muted-foreground">
          {t("partnerWhatsappInbox.messages.readOnlyHint")}
        </p>
      </footer>
    </div>
  );
};
